<script lang="ts" setup>
import PackageAdditionalServiceEnum from '~/enums/PackageAdditionalServiceEnum'
import { useSiteProContentStore } from '~/store/shipzeeProContentStore'
import toPrice from '~/utils/toPrice'
import type { PackageInterface } from '~/types/shipzeePro/packagesTypes'

const { t } = useLang()
const isVisible = ref(false)

const shipzeeProContent = useSiteProContentStore()

const additionalServices = computed(() => shipzeeProContent.additionalServicesConfig)

const props = defineProps<{
  item: PackageInterface
}>()

const emit = defineEmits(['openModal'])

const selectedService = ref<PackageAdditionalServiceEnum | null>(null)

const servicesAlreadyDone = computed(() => {
  return props.item.additional_services ? props.item.additional_services.map((service) => service.service_id) : []
})

const notProcessedService = computed(() => props.item.additional_services.filter((service) => !service.completed)[0])

const notProcessedServiceTranslation = computed(() => {
  if (!notProcessedService.value) {
    return null
  }

  const serviceNameTranslation: string = getServiceNameById(notProcessedService.value.service_id)

  return t('site_pro.table.statuses.request_being_processed', {
    service_name: serviceNameTranslation,
  })
})

function getServiceNameById(serviceId: PackageAdditionalServiceEnum) {
  switch (serviceId) {
    case PackageAdditionalServiceEnum.InspectPackage:
      return t('site_pro.additional_services.inspect_package.title')
    case PackageAdditionalServiceEnum.ExtraPhotos:
      return t('site_pro.additional_services.extra_photos.title')
    case PackageAdditionalServiceEnum.ReturnToSender:
      return t('site_pro.additional_services.return_to_sender.title')
    case PackageAdditionalServiceEnum.AbandonPackage:
      return t('site_pro.additional_services.abandon_package.title')
  }
}

function onServiceClick(id: PackageAdditionalServiceEnum) {
  emit('openModal', id, props.item.id)
}
</script>

<template>
  <div>
    <div class="select-services flex flex-col w-full">
      <label class="formkit-label !font-normal !font-secondary !text-xs !pb-1">
        <span v-if="!notProcessedService">{{ t('site_pro.additional_services.label') }}</span>
        <span v-else>{{ t('site_pro.additional_service') }}</span>
      </label>

      <UPopover
        v-model:open="isVisible"
        mode="click"
        :ui="{
          base: 'dropdown-body',
          container: 'dropdown',
        }"
        :popper="{ placement: 'bottom-start' }">
        <div
          class="formkit-inner service text-text2 dark:text-dmText2 bg-white dark:bg-dmGray1 flex justify-between items-center w-full"
          :class="{ 'bg-neutral8': notProcessedServiceTranslation }">
          <span class="text-text1 dark:text-dmText1 font-base text-sm py-[2px]">
            <template v-if="notProcessedServiceTranslation">
              {{ getServiceNameById(notProcessedService.service_id) }}
            </template>
            <span v-else>{{ t('site_pro.additional_services.select_services') }}</span>
          </span>
          <i v-if="!notProcessedService" class="fas fa-caret-down ml-1 mr-1" />
        </div>
        <template #panel>
          <div
            v-if="!servicesAlreadyDone.includes(PackageAdditionalServiceEnum.ExtraPhotos)"
            class="dropdown-item"
            :class="{ active: selectedService === PackageAdditionalServiceEnum.ExtraPhotos }"
            @click="onServiceClick(PackageAdditionalServiceEnum.ExtraPhotos)">
            <div class="item-wrapper">
              <div class="flex items-center justify-between font-medium">
                <span>{{ t('site_pro.additional_services.extra_photos.title') }}</span>
                <span>{{ toPrice(additionalServices?.request_additional_photos.price) }}</span>
              </div>
              <div class="item-description">
                {{ t('site_pro.additional_services.extra_photos.dropdown_description') }}
              </div>
            </div>
          </div>
          <div
            v-if="!servicesAlreadyDone.includes(PackageAdditionalServiceEnum.AbandonPackage)"
            class="dropdown-item"
            :class="{ active: selectedService === PackageAdditionalServiceEnum.AbandonPackage }"
            @click="onServiceClick(PackageAdditionalServiceEnum.AbandonPackage)">
            <div class="item-wrapper">
              <div class="flex items-center justify-between font-medium">
                <span>{{ t('site_pro.additional_services.abandon_package.title') }}</span>
                <span>{{ toPrice(additionalServices?.abandon_package.price) }}</span>
              </div>
              <div class="item-description">
                {{ t('site_pro.additional_services.abandon_package.dropdown_description') }}
              </div>
            </div>
          </div>
        </template>
      </UPopover>
    </div>
  </div>
</template>

<style lang="postcss" scoped>
.select-services {
  @apply xl:min-w-[350px];
  img {
    height: 15px;
    border-radius: 3px;
  }

  .service {
    padding: 10.5px;
    font-size: 0.875rem;

    span {
      line-height: 1;
    }
  }

  :deep(.dropdown) {
    .dropdown-body {
      @apply flex flex-col overflow-y-auto py-0 px-0 max-w-[500px] max-h-[21.875rem];

      .dropdown-item {
        @apply flex px-[1.25rem] hover:bg-neutral7 dark:hover:bg-dmGray2 cursor-pointer;
      }

      .dropdown-item + .dropdown-item {
        @apply border-t border-solid border-neutral6 dark:border-dmGray1 mt-0;
      }

      .item-wrapper {
        @apply flex flex-col w-full pt-4;
      }

      .item-description {
        @apply pt-2 pb-4 whitespace-pre-line text-sm leading-base;
      }
    }
  }
}
</style>
